import React from 'react';
import { Trans } from 'react-i18next';
import { Text } from 'mintel-react-ui';

import { HELPDESK_EMAIL } from '../shared/constants';

type Args = { [key: string]: string };

const messages: {
  [key: string]: (args: Args) => Announcement;
} = {
  loginInfo: (): Announcement => ({
    label: null,
    message: (
      <Text.Body2>
        <Trans>
          Enter your company or institution email address. Next you&apos;ll be
          asked for your Mintel password, or sent to your company or institution
          website to sign in with your company or institution login details.
          Note: Shibboleth users should use the Federated Login.
        </Trans>
      </Text.Body2>
    ),
  }),
  notRecognized: ({ registerLink }: Args): Announcement => ({
    label: <Trans>Your email or password wasn&apos;t recognized</Trans>,
    message: (
      <Trans i18nKey="activateAccount">
        Please <a href={registerLink}>activate your account</a> if you are new
        to Mintel.
      </Trans>
    ),
  }),
  emailAlreadyRegistered: ({
    email,
    changePasswordLink,
  }: Args): Announcement => ({
    label: (
      <Trans>The email address {email} has already been registered.</Trans>
    ),
    message: (
      <Trans>
        You can either <a href={changePasswordLink}>change your password</a> or{' '}
        <a href={`mailto:${HELPDESK_EMAIL}`}>contact Helpdesk</a> if you need
        further assistance.
      </Trans>
    ),
  }),
  federatedLoginByEmail: (): Announcement => ({
    label: (
      <Trans>
        Thank you for trying to log in. Since your account has no password, we
        have sent you an email that will allow you access to Mintel&apos;s
        services.
      </Trans>
    ),
    message: (
      <Trans>
        Once you have received the email, simply follow the link inside the
        email to log in.
      </Trans>
    ),
  }),
  domainNotRecognized: (): Announcement => ({
    label: (
      <Trans>
        Our systems do not recognize you as part of any client organization, so
        we are unable to grant you access to our services. If you think you
        should have access to Mintel services, please{' '}
        <a href={`mailto:${HELPDESK_EMAIL}`}>contact Helpdesk</a>.
      </Trans>
    ),
    message: (
      <Trans>
        To find out more about how our market insight can help your business,{' '}
        <a href="https://www.mintel.com/contact-us">please register here</a>.
      </Trans>
    ),
  }),
  domainNotConfigured: ({ email }: Args): Announcement => ({
    label: (
      <Trans>
        The Email domain associated with {email} is not configured for SSO
        login.
      </Trans>
    ),
    message: (
      <Trans>
        You can either register/log in directly with your email and password or{' '}
        <a href={`mailto:${HELPDESK_EMAIL}`}>contact Helpdesk</a> if you need
        further assistance.
      </Trans>
    ),
  }),
  registerEmailSent: ({ email }: Args): Announcement => ({
    label: (
      <Trans>
        We have sent an email to {email} containing a link. Please click once on
        the link it contains.
      </Trans>
    ),
    message: (
      <Trans>
        If you continue to experience problems, please contact Helpdesk at{' '}
        <a href={`mailto:${HELPDESK_EMAIL}`}>info@mintel.com</a> for assistance.
      </Trans>
    ),
  }),
  resetPasswordEmailSent: ({ email }: Args): Announcement => ({
    label: (
      <Trans>
        An email has been sent to {email} containing instructions for changing
        your password.
      </Trans>
    ),
    message: (
      <Trans>
        If you are still having problems please contact the Mintel helpdesk at
        <a href={`mailto:${HELPDESK_EMAIL}`}>helpdesk@mintel.com</a>.
      </Trans>
    ),
  }),
  resetPasswordFederatedUser: (): Announcement => ({
    label: (
      <Trans>
        Thank you for trying to log in. Since your account has no password, we
        have sent you an email that will allow you to access Mintel services
        whilst out of the office.
      </Trans>
    ),
    message: (
      <Trans>
        Once you have received the email, simply follow the link inside the
        email to log in.
      </Trans>
    ),
  }),
};

export default messages;
